/* eslint-disable import/no-extraneous-dependencies */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  AnimatedBox,
  ArrowRightIcon,
  Box,
  FrameworkImage,
  LeftArrowIcon,
  Pressable,
  RightArrowIcon,
  Typography,
  useSpring,
} from '@ttx/design-system';
import {FlatList, View, StyleSheet, Animated} from 'react-native';
import {NftToken} from '../../../../apps/web/server/router/collection/types';
import {useBreakpoint} from '../use-breakpoint';
import {useButtonAnimation} from '../button/use-button-animation';
import {InputState, useInteractionState} from '../button/use-interaction-state';

export interface PurchaseTransitionModalProps {
  onClose: () => void;
  purchaseNfts: NftToken[];
}

interface ImageCarouselItem {
  id: string;
  image?: string;
  name?: string;
}

interface ImageCarouselProps {
  data: ImageCarouselItem[];
}

const getViewConfig = (isMobileView = false) => {
  const {width, height} = {
    width: isMobileView ? window.innerWidth : 640,
    height: 200,
  }; // Dimensions.get('window');
  const SPACING = 5;
  const ITEM_LENGTH = width * 0.8; // Item is a square. Therefore, its height and width are of the same length.
  const EMPTY_ITEM_LENGTH = (width - ITEM_LENGTH) / 2;
  const BORDER_RADIUS = 20;
  const CURRENT_ITEM_TRANSLATE_Y = 0;
  const styles = StyleSheet.create({
    container: {},
    arrowBtn: {},
    arrowBtnText: {
      fontSize: 42,
      fontWeight: '600',
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: 10,
    },
    flatListContent: {
      height: CURRENT_ITEM_TRANSLATE_Y * 2 + ITEM_LENGTH,
      alignItems: 'center',
      marginBottom: CURRENT_ITEM_TRANSLATE_Y,
    },
    flatListContent1: {
      height: '350px',
      alignItems: 'center',
      marginBottom: CURRENT_ITEM_TRANSLATE_Y,
    },
    item: {},
    itemContent: {
      // marginHorizontal: SPACING * 3,
      alignItems: 'center',
      backgroundColor: 'white',
      borderRadius: BORDER_RADIUS + SPACING * 2,
      marginRight: '27px',
    },
    itemContent1: {
      alignItems: 'center',
      backgroundColor: 'tranparent',
      borderRadius: BORDER_RADIUS + SPACING * 2,
      paddingRight: '27px',
    },
    itemText: {
      fontSize: 24,
      position: 'absolute',
      bottom: SPACING * 2,
      right: SPACING * 2,
      color: 'white',
      fontWeight: '600',
    },
    itemImage: {
      width: '100%',
      height: ITEM_LENGTH,
      borderRadius: BORDER_RADIUS,
      // resizeMode: 'cover',
    },
  });
  return {
    styles,
    height,
    SPACING,
    ITEM_LENGTH,
    EMPTY_ITEM_LENGTH,
    BORDER_RADIUS,
    CURRENT_ITEM_TRANSLATE_Y,
  };
};

export const PurchaseTransitionModal: React.FC<
  PurchaseTransitionModalProps
> = ({onClose, purchaseNfts}: PurchaseTransitionModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnimation, setShowAnimation] = useState(true);
  const [fadeAnimation, setFadeAnimation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {currentBreakpoint} = useBreakpoint();
  const isMobile = currentBreakpoint === '768px';
  const currentIndex1 = useRef<number>(0);
  const flatListRef = useRef<FlatList<any>>(null);
  const flatTextListRef = useRef<FlatList<any>>(null);
  const {
    styles,
    height,
    ITEM_LENGTH,
    EMPTY_ITEM_LENGTH,
    BORDER_RADIUS,
    CURRENT_ITEM_TRANSLATE_Y,
  } = getViewConfig(isMobile);

  useEffect(() => {
    setTimeout(() => {
      setShowAnimation(false);
    }, 3000);
  }, []);

  const onNext = () => {
    if (currentIndex1.current !== purchaseNfts.length) {
      if (flatListRef.current) {
        flatListRef.current.scrollToIndex({
          animated: true,
          index: currentIndex1.current + 1,
        });
      }
      if (flatTextListRef.current) {
        flatTextListRef.current.scrollToIndex({
          animated: true,
          index: currentIndex1.current + 1,
        });
      }
    }

    if (currentIndex < purchaseNfts.length - 1) {
      setFadeAnimation(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setFadeAnimation(false);
      }, 600);
    }
  };
  const onPrevious = () => {
    if (currentIndex1.current > 0) {
      if (flatListRef.current) {
        flatListRef.current.scrollToIndex({
          animated: true,
          index: currentIndex,
        });
      }
      if (flatTextListRef.current) {
        flatTextListRef.current.scrollToIndex({
          animated: true,
          index: currentIndex,
        });
      }
    }
    if (currentIndex > 0) {
      setFadeAnimation(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex - 1);
        setFadeAnimation(false);
      }, 600);
    }
  };

  const modalStyle = useSpring({
    opacity: fadeAnimation ? 0 : 1,
    config: {
      duration: 500,
    },
  });

  const animationStyle = showAnimation
    ? {
        height: '55vh',
        marginTop: '10%',
        backgroundRepeat: 'no-repeat',
        backgroundImage: "url('/drum-roll-animation.gif')",
        backgroundPosition: 'center',
        backgroundSize: 'contain',
      }
    : {};

  const fadeStyle = useSpring({
    opacity: fadeAnimation ? 0 : 1,
    marginTop: fadeAnimation ? '200px' : '0px',
    from: {marginTop: '0px'},
    config: {
      duration: 300,
      mass: 1,
      stiffness: 64,
      damping: 12,
    },
  });

  const fadeStyleImage = useSpring({
    opacity: fadeAnimation ? 0 : 1,
    left: fadeAnimation ? '-80px' : '0px',
    config: {
      duration: 300,
    },
  });

  const {state, events} = useInteractionState();

  const {animatedAccessoryStyle} = useButtonAnimation(state);

  const isFirstIndex = currentIndex === 0;
  const isLastIndex = currentIndex === purchaseNfts.length - 1;

  // Animation

  const scrollX = useRef(new Animated.Value(0)).current;
  const [dataWithPlaceholders, setDataWithPlaceholders] = useState<any>([]);

  const getItemLayout = (_data: any, index: number) => ({
    length: ITEM_LENGTH,
    offset: ITEM_LENGTH * (index - 1),
    index,
  });

  const handleOnViewableItemsChanged = useCallback(
    // @ts-expect-error type fix
    ({viewableItems}) => {
      const itemsInView = viewableItems.filter(
        ({item}: {item: ImageCarouselItem}) => item.image && item.name,
      );

      if (itemsInView.length === 0) {
        return;
      }

      currentIndex1.current = itemsInView[0].index;
    },
    [purchaseNfts],
  );
  const viewabilityConfig = {
    itemVisiblePercentThreshold: 100,
  };
  const viewabilityConfigCallbackPairs = useRef([
    {viewabilityConfig, onViewableItemsChanged: handleOnViewableItemsChanged},
  ]);

  useEffect(() => {
    setDataWithPlaceholders([...purchaseNfts, {name: 'first'}]);
    currentIndex1.current = 1;
  }, [purchaseNfts]);

  // End Animation
  const scrollY = React.useRef(new Animated.Value(0)).current;
  return (
    <Box
      borderColor="outlines"
      style={{
        height: isMobile ? '100vh' : '99vh',
        backgroundColor: '#000',
        margin: 0,
        width: isMobile ? '100vw' : '1150px',
      }}
      borderWidth="zero"
      borderStyle="solid"
      padding={isMobile ? 'one' : 'four'}
      paddingTop={isMobile ? 'twenty' : 'zero'}
    >
      {showAnimation && (
        <AnimatedBox style={modalStyle}>
          <Box>
            <Box style={{...animationStyle}}>-</Box>
            <Box>
              <Typography
                fontSize="s"
                color="textDefault"
                style={{margin: 'auto'}}
              >
                <ActivityIndicator color="brandPrimary" size={14} />
                &nbsp;&nbsp;Loading assets
              </Typography>
            </Box>
          </Box>
        </AnimatedBox>
      )}
      {!isMobile && (
        <Box
          flexDirection="row"
          top="150px"
          style={!showAnimation ? {} : {opacity: 0, height: '0px'}}
        >
          <Box flex={1}>
            {/* <Box paddingBottom="four">
              <Typography color="textDefault" textStyle="m">
                #{currentIndex + 1}
              </Typography>
            </Box>
            <AnimatedBox style={fadeStyle}>
              <Typography color="textDefault" textStyle="h1">
                {purchaseNfts[currentIndex].name}
              </Typography>
            </AnimatedBox> */}
            <>
              <Box height="300px">
                {/* @ts-expect-error react-native fix */}
                <Animated.FlatList
                  ref={flatTextListRef}
                  onScroll={Animated.event(
                    [{nativeEvent: {contentOffset: {y: scrollY}}}],
                    {useNativeDriver: true},
                  )}
                  data={dataWithPlaceholders}
                  keyExtractor={(item) => item.id}
                  renderItem={({item, index}) => {
                    // Normal Animation
                    const inputRange = [
                      (index - 2) * ITEM_LENGTH,
                      (index - 1) * ITEM_LENGTH,
                      index * ITEM_LENGTH,
                    ];
                    const scale = 1;
                    const opacity = scrollY.interpolate({
                      inputRange,
                      outputRange: [1, 1, 1],
                      extrapolate: 'clamp',
                    });
                    const Offset = scrollY.interpolate({
                      inputRange,
                      outputRange: [0, 0, 0],
                      extrapolate: 'clamp',
                    });

                    return (
                      <Animated.View
                        style={{
                          transform: [{scale}, {translateX: Offset}],
                          height: '300px',
                          opacity,
                        }}
                      >
                        <Box>
                          <Box paddingBottom="four">
                            <Typography color="textDefault" textStyle="m">
                              #{currentIndex + 1}
                            </Typography>
                          </Box>
                          <AnimatedBox>
                            <Typography color="textDefault" textStyle="h1">
                              {item.name}
                            </Typography>
                          </AnimatedBox>
                        </Box>
                      </Animated.View>
                    );
                  }}
                  bounces={false}
                  decelerationRate={0}
                  renderToHardwareTextureAndroid
                  scrollEnabled={false}
                  scrollEventThrottle={16}
                  viewabilityConfigCallbackPairs={
                    viewabilityConfigCallbackPairs.current
                  }
                  viewabilityConfig={viewabilityConfig}
                />
              </Box>
            </>
            <Box style={{position: 'absolute', bottom: '0px'}}>
              <Box flexDirection="row">
                <Pressable
                  onPress={onPrevious}
                  accessibilityState={{
                    disabled: isFirstIndex,
                  }}
                >
                  <Box
                    width={35}
                    height={35}
                    borderRadius="circle"
                    borderWidth="one"
                    borderColor={isFirstIndex ? 'textLow' : 'outlines'}
                    borderStyle="solid"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    marginLeft="zero"
                  >
                    <LeftArrowIcon
                      size={12}
                      color={isFirstIndex ? 'textLow' : 'outlines'}
                    />
                  </Box>
                </Pressable>
                <Pressable
                  onPress={onNext}
                  accessibilityState={{
                    disabled: isLastIndex,
                  }}
                >
                  <Box
                    width={35}
                    height={35}
                    borderRadius="circle"
                    borderWidth="one"
                    borderColor={isLastIndex ? 'textLow' : 'outlines'}
                    borderStyle="solid"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    style={{marginLeft: '12px'}}
                  >
                    <RightArrowIcon
                      size={12}
                      color={isLastIndex ? 'textLow' : 'outlines'}
                    />
                  </Box>
                </Pressable>
                <Pressable
                  onPress={() => {
                    if (!isLoading) {
                      onClose();
                      setIsLoading(true);
                    }
                  }}
                  {...events}
                  hovered={state === InputState.Hovering}
                  pressed={state === InputState.Pressed}
                  accessibilityState={{
                    selected: state === InputState.Pressed,
                  }}
                >
                  <Box
                    width={110}
                    height={35}
                    borderRadius="four"
                    borderWidth="one"
                    borderColor="outlines"
                    borderStyle="solid"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    style={{marginLeft: '24px'}}
                    backgroundColor={
                      isLastIndex ? 'brandPrimary' : 'transparent'
                    }
                  >
                    {isLoading ? (
                      <ActivityIndicator
                        color={isLastIndex ? 'outlinesInvert' : 'outlines'}
                      />
                    ) : (
                      <Box
                        opacity={isLoading ? 0 : 1}
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {isLastIndex && (
                          <AnimatedBox style={animatedAccessoryStyle}>
                            <ArrowRightIcon color="surfaceDark" />
                          </AnimatedBox>
                        )}
                        <Typography
                          color={isLastIndex ? 'textInvert' : 'textDefault'}
                          textStyle="s"
                        >
                          {isLastIndex ? 'My NFT' : 'Skip'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Pressable>
              </Box>
            </Box>
          </Box>
          <Box
            m="lg"
            flex={1}
            style={{margin: 'auto'}}
            justifyContent="space-between"
          >
            {/* @ts-expect-error react-native fix */}
            <FlatList
              ref={flatListRef}
              data={dataWithPlaceholders}
              renderItem={({item, index}) => {
                if (!item.image || !item.name) {
                  // @ts-expect-error react-native fix
                  return <View style={{width: EMPTY_ITEM_LENGTH}} />;
                }

                const inputRange = [
                  (index - 2) * ITEM_LENGTH,
                  (index - 1) * ITEM_LENGTH,
                  index * ITEM_LENGTH,
                ];

                const translateY = scrollX.interpolate({
                  inputRange,
                  outputRange: [
                    CURRENT_ITEM_TRANSLATE_Y * 2,
                    CURRENT_ITEM_TRANSLATE_Y,
                    CURRENT_ITEM_TRANSLATE_Y * 2,
                  ],
                  extrapolate: 'clamp',
                });

                return (
                  // @ts-expect-error react-native fix
                  <View style={{width: ITEM_LENGTH}}>
                    <Animated.View
                      style={[
                        {
                          transform: [{translateY}],
                        },
                        styles.itemContent,
                      ]}
                    >
                      <Box
                        style={{
                          width: '100%',
                          height: ITEM_LENGTH,
                          borderRadius: BORDER_RADIUS,
                          // resizeMode: 'cover',
                        }}
                      >
                        <FrameworkImage
                          src={item.image}
                          alt={item.name}
                          layout="fill"
                          objectFit="cover"
                        />
                      </Box>
                    </Animated.View>
                  </View>
                );
              }}
              getItemLayout={getItemLayout}
              horizontal
              showsHorizontalScrollIndicator={false}
              keyExtractor={(item) => item.id}
              bounces={false}
              decelerationRate={0}
              renderToHardwareTextureAndroid
              contentContainerStyle={styles.flatListContent}
              snapToInterval={ITEM_LENGTH}
              snapToAlignment="start"
              onScroll={Animated.event(
                [{nativeEvent: {contentOffset: {x: scrollX}}}],
                {useNativeDriver: false},
              )}
              scrollEnabled={false}
              scrollEventThrottle={16}
              // onViewableItemsChanged={handleOnViewableItemsChanged}
              viewabilityConfigCallbackPairs={
                viewabilityConfigCallbackPairs.current
              }
              viewabilityConfig={viewabilityConfig}
            />
          </Box>
        </Box>
      )}
      {isMobile && (
        <>
          <Box
            style={
              !showAnimation ? {} : {opacity: 0, height: '0px', margin: 'auto'}
            }
            marginTop="five"
            justifyContent="space-between"
            padding="zero"
            paddingTop="twenty"
          >
            {/* @ts-expect-error react-native fix */}
            <FlatList
              ref={flatListRef}
              data={dataWithPlaceholders}
              renderItem={({item, index}) => {
                if (!item.image || !item.name) {
                  // @ts-expect-error react-native fix
                  return <View style={{width: EMPTY_ITEM_LENGTH}} />;
                }

                const inputRange = [
                  (index - 2) * ITEM_LENGTH,
                  (index - 1) * ITEM_LENGTH,
                  index * ITEM_LENGTH,
                ];

                const translateY = scrollX.interpolate({
                  inputRange,
                  outputRange: [
                    CURRENT_ITEM_TRANSLATE_Y * 2,
                    CURRENT_ITEM_TRANSLATE_Y,
                    CURRENT_ITEM_TRANSLATE_Y * 2,
                  ],
                  extrapolate: 'clamp',
                });

                return (
                  // @ts-expect-error react-native fix
                  <View style={{width: ITEM_LENGTH}}>
                    <Animated.View
                      style={[
                        {
                          transform: [{translateY}],
                        },
                        styles.itemContent1,
                      ]}
                    >
                      <Box
                        style={{
                          width: '100%',
                          height: ITEM_LENGTH,
                          borderRadius: BORDER_RADIUS,
                          // resizeMode: 'cover',
                        }}
                      >
                        <FrameworkImage
                          src={item.image}
                          alt={item.name}
                          layout="fill"
                          objectFit="cover"
                        />
                      </Box>
                    </Animated.View>
                  </View>
                );
              }}
              getItemLayout={getItemLayout}
              horizontal
              showsHorizontalScrollIndicator={false}
              keyExtractor={(item) => item.id}
              bounces={false}
              decelerationRate={0}
              renderToHardwareTextureAndroid
              contentContainerStyle={styles.flatListContent}
              snapToInterval={ITEM_LENGTH}
              snapToAlignment="start"
              onScroll={Animated.event(
                [{nativeEvent: {contentOffset: {x: scrollX}}}],
                {useNativeDriver: false},
              )}
              scrollEnabled={false}
              scrollEventThrottle={16}
              // onViewableItemsChanged={handleOnViewableItemsChanged}
              viewabilityConfigCallbackPairs={
                viewabilityConfigCallbackPairs.current
              }
              viewabilityConfig={viewabilityConfig}
            />
          </Box>
          <Box
            style={!showAnimation ? {} : {opacity: 0, height: '0px'}}
            marginTop="one"
          >
            {/* <Box paddingBottom="two">
              <Typography color="textDefault" textStyle="s">
                #{currentIndex + 1}
              </Typography>
            </Box>
            <AnimatedBox style={fadeStyle}>
              <Typography color="textDefault" textStyle="h2">
                {purchaseNfts[currentIndex].name}
              </Typography>
            </AnimatedBox> */}
            <>
              <Box height="500px">
                {/* @ts-expect-error react-native fix */}
                <Animated.FlatList
                  ref={flatTextListRef}
                  onScroll={Animated.event(
                    [{nativeEvent: {contentOffset: {y: scrollY}}}],
                    {useNativeDriver: true},
                  )}
                  data={dataWithPlaceholders}
                  keyExtractor={(item) => item.id}
                  renderItem={({item, index}) => {
                    // Normal Animation
                    const inputRange = [
                      (index - 2) * ITEM_LENGTH,
                      (index - 1) * ITEM_LENGTH,
                      index * ITEM_LENGTH,
                    ];
                    const scale = 1;
                    const opacity = scrollY.interpolate({
                      inputRange,
                      outputRange: [1, 1, 1],
                      extrapolate: 'clamp',
                    });
                    const Offset = scrollY.interpolate({
                      inputRange,
                      outputRange: [0, 0, 0],
                      extrapolate: 'clamp',
                    });

                    return (
                      <Animated.View
                        style={{
                          transform: [{scale}, {translateX: Offset}],
                          height: '500px',
                          opacity,
                        }}
                      >
                        <Box>
                          <Box paddingBottom="four">
                            <Typography color="textDefault" textStyle="m">
                              #{currentIndex + 1}
                            </Typography>
                          </Box>
                          <AnimatedBox>
                            <Typography color="textDefault" textStyle="h1">
                              {item.name}
                            </Typography>
                          </AnimatedBox>
                        </Box>
                      </Animated.View>
                    );
                  }}
                  bounces={false}
                  decelerationRate={0}
                  renderToHardwareTextureAndroid
                  scrollEnabled={false}
                  scrollEventThrottle={16}
                  viewabilityConfigCallbackPairs={
                    viewabilityConfigCallbackPairs.current
                  }
                  viewabilityConfig={viewabilityConfig}
                />
              </Box>
            </>
          </Box>
          {!showAnimation && (
            <Box margin="one" position="absolute" bottom="60px" width="100%">
              <Box flexDirection="row">
                <Pressable
                  onPress={onPrevious}
                  accessibilityState={{
                    disabled: isFirstIndex,
                  }}
                >
                  <Box
                    width={35}
                    height={35}
                    borderRadius="circle"
                    borderWidth="one"
                    borderColor={isFirstIndex ? 'textLow' : 'outlines'}
                    borderStyle="solid"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                  >
                    <LeftArrowIcon
                      size={12}
                      color={isFirstIndex ? 'textLow' : 'outlines'}
                    />
                  </Box>
                </Pressable>
                <Pressable
                  onPress={onNext}
                  accessibilityState={{
                    disabled: isLastIndex,
                  }}
                >
                  <Box
                    width={35}
                    height={35}
                    borderRadius="circle"
                    borderWidth="one"
                    borderColor={isLastIndex ? 'textLow' : 'outlines'}
                    borderStyle="solid"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    style={{marginLeft: '12px'}}
                  >
                    <RightArrowIcon
                      size={12}
                      color={isLastIndex ? 'textLow' : 'outlines'}
                    />
                  </Box>
                </Pressable>
                <Box width="100%">
                  <Pressable
                    style={{
                      right: '130px',
                      position: 'absolute',
                      width: '140px',
                    }}
                    onPress={onClose}
                  >
                    <Box
                      width={140}
                      height={35}
                      borderRadius="four"
                      borderWidth="one"
                      borderColor="outlines"
                      borderStyle="solid"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      marginLeft="five"
                      backgroundColor={
                        isLastIndex ? 'brandPrimary' : 'transparent'
                      }
                    >
                      <Typography
                        color={isLastIndex ? 'textInvert' : 'textDefault'}
                        textStyle="s"
                      >
                        {isLastIndex ? 'My NFT' : 'Skip'}
                      </Typography>
                    </Box>
                  </Pressable>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
